import axios from "axios"; // eslint-disable-line check-axios-usage/no-direct-axios
import * as constants from "Constants";
import { authLogout } from "Store/UserInfo/Actions";
import displayGlobalMessage from "Utils/DisplayGlobalMessage";
import displayErrorWithReport from "Utils/DisplayErrorWithReport";

axios.defaults.baseURL = constants.base_url; // eslint-disable-line check-axios-usage/no-direct-axios

var instance = axios.create(); // eslint-disable-line check-axios-usage/no-direct-axios
const pendingRequests = [];
const pendingThreadRequests = [];
const pendingCommentRequests = [];

const logoutTimerId = null;

function removePending(config) {
  let index = pendingRequests.findIndex((request) => request === config);
  if (index !== -1) {
    pendingRequests.splice(index, 1);
  }
  index = pendingCommentRequests.findIndex((request) => request === config);
  if (index !== -1) {
    pendingCommentRequests.splice(index, 1);
  }
  index = pendingThreadRequests.findIndex((request) => request === config);
  if (index !== -1) {
    pendingThreadRequests.splice(index, 1);
  }
}

const validateTokenWithFetch = async () => {
  try {
    const headers = instance.defaults.headers.common;
    const response = await fetch(constants.base_url + '/accounts/whoami/', {
      method: 'GET',
      headers: headers
    });

    console.log('validation', response)

    if (response.status === 403) {
      return { isValid: false };
    } else if (response.ok) {
      return { isValid: true };
    } else {
      return { isValid: false, error: 'Unexpected response status' };
    }
  } catch (error) {
    console.error('Fetch validation error:', error);
    return { isValid: false, error };
  }
};

const createAxiosInstance = (dispatch) => {
  instance.interceptors.response.use(
    (response) => {
      // Remove the request object from the array on success
      removePending(response.config);
      return response;
    }, async (error) => {
      const expectedError =
        error.response &&
        error.response.status >= 400 &&
        error.response.status < 500;

      if (!expectedError) {
        // logger.log(error.response);
        // displayGlobalMessage(`An unexpected error occurred with status code: ${error?.response?.status} ${error?.response?.message || error?.response?.statusText || ''}`, "error");
      } else if (error.response.status === 401) {
        clearTimeout(logoutTimerId);
        // logoutTimerId = setTimeout(() => {
        //   displayGlobalMessage("Session Expired. Logging out...", "error");
        //   dispatch(logout());
        // }, 500);
        const tokenValidity = await validateTokenWithFetch();
        if (!tokenValidity.isValid) {
          displayGlobalMessage("Session Expired. Logging out...", "error", true);
          dispatch(authLogout());
        }
      } else if (error.response.status === 403 && error.response.data?.detail === "This quiz is not currently available.") {
        displayGlobalMessage("This quiz is not yet available. Please check the start time.", "warning");
      } else if (process.env.REACT_APP_ENABLE_ERROR_REPORT_V2 === 'true' && !error.config?.skipErrorToast) {
        // V2: Show all errors (4xx and 5xx) with report option
        // Skip if request was configured with skipErrorToast: true
        const data = error.response.data;

        // User-friendly message for toast (don't show raw backend errors)
        const userMessage = 'Something went wrong';

        // Technical details for error report modal
        let technicalDetails = 'An error occurred';
        if (typeof data === 'string') {
          technicalDetails = data;
        } else if (data?.message) {
          technicalDetails = data.message;
        } else if (data?.detail) {
          technicalDetails = data.detail;
        } else if (data?.error) {
          technicalDetails = data.error;
        } else if (data?.errors) {
          technicalDetails = typeof data.errors === 'string'
            ? data.errors
            : JSON.stringify(data.errors);
        } else if (data?.msg) {
          technicalDetails = data.msg;
        } else if (data?.non_field_errors) {
          technicalDetails = Array.isArray(data.non_field_errors)
            ? data.non_field_errors.join(', ')
            : data.non_field_errors;
        }

        displayErrorWithReport(userMessage, {
          endpoint: error.config?.url,
          stackTrace: technicalDetails,
        });

        // Mark error as handled so components don't show duplicate toasts
        error._errorReportHandled = true;
      }

      return Promise.reject(error);
    });

  instance.interceptors.request.use(
    (config) => {
      // log.log({ check_config: config })
      pendingRequests.push(config);
      let url = config.url || "";
      if (url.includes("forum/") && url.includes("api/")) {
        if (url.includes("threads/")) {
          pendingThreadRequests.push(config);
        } else if (url.includes("comments/")) {
          pendingCommentRequests.push(config);
        }
      }
      return config;
    },
    (error) => {
      Promise.reject(error);
    }
  );
}

function setJwt(jwt) {
  instance.defaults.headers.common["Authorization"] = jwt;
  //log.log("instance is", instance.defaults.headers)
}

function clearJwt() {
  delete instance.defaults.headers.common["Authorization"];
}

const setupAxiosInterceptors = (dispatch) => {
  createAxiosInstance(dispatch);
};

const setupAxiosDefaults = ({ baseURL, headers }) => {
  instance.defaults.baseURL = baseURL;
  instance.defaults.headers.common = headers;
  return instance;
}

const postWithoutAuth = (url, data, config = {}) => {
  // Create a new config object that explicitly removes Authorization
  const configWithoutAuth = {
    ...config,
    headers: {
      ...config.headers,
      'Authorization': undefined // This will remove the header entirely
    }
  };

  return instance.post(url, data, configWithoutAuth);
};

export { setupAxiosInterceptors, setupAxiosDefaults };

export default {
  get: instance.get,
  post: instance.post,
  postWithoutAuth: postWithoutAuth,
  put: instance.put,
  delete: instance.delete,
  request: instance.request,
  patch: instance.patch,
  setJwt,
  clearJwt,
  pendingRequests,
  pendingCommentRequests,
  pendingThreadRequests,
  instance: instance,
  defaults: instance.defaults,
  all: axios.all, // eslint-disable-line check-axios-usage/no-direct-axios
  spread: axios.spread, // eslint-disable-line check-axios-usage/no-direct-axios
};
